import React from 'react';

import { classnames, margin } from '@lumapps/classnames';
import { EditLinkPreviewDialog } from '@lumapps/lumx-preview/components/LinkPreview/EditLinkPreviewDialog';
import { mdiAutorenew } from '@lumapps/lumx/icons';
import { AspectRatio, Kind, Message, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';
import { WREX_CORE } from '@lumapps/wrex-core/keys';
import { ReactEditor, useSelected, useSlateStatic } from '@lumapps/wrex-core/slate';
import { useInlineVoid } from '@lumapps/wrex-core/slate/utils/useInlineVoid';
import { ElementRender } from '@lumapps/wrex-core/types';

import { LINK_PREVIEW } from '../../../constants';
import type { LinkPreviewEditor, LinkPreviewEditorOptions, LinkPreviewElement } from '../../../types';
import { CLASSNAME, LinkPreview } from '../../blocks/LinkPreview';
import { ActionButtons } from '../../buttons/ActionButtons';
import { ReplaceResourcePreviewDialog } from '../../dialog/ReplaceResourcePreviewDialog';

import './index.scss';

export const EXTERNAL_RESOURCE_IMG_CONFIG = {
    aspectRatio: AspectRatio.horizontal,
    size: Size.xl,
};

type EditableLinkPreviewType = ElementRender<LinkPreviewElement & LinkPreviewEditorOptions, HTMLDivElement>;

export const EditableLinkPreview: EditableLinkPreviewType = ({
    element,
    children,
    className,
    isEditable = false,
    ...forwardedProps
}) => {
    const editor = useSlateStatic() as ReactEditor & LinkPreviewEditor;
    const selected = useSelected();
    const { onFocus, onDelete } = useInlineVoid(editor, element as LinkPreviewElement);
    const { translateKey } = useTranslate();
    const [isUpdateDialogOpen, toggleUpdateDialog, closeUpdateDialog] = useBooleanState(false);
    const parentRef = React.useRef(null);

    const path = ReactEditor.findPath(editor, element as LinkPreviewElement);
    const pathRef = editor.pathRef(path);

    const handlePreviewChange = React.useCallback(
        (link) => {
            editor.updateLinkPreview(
                {
                    ...link,
                    thumbnailUrl: link.resource
                        ? link.thumbnailUrl
                        : link.images && link.images[link.thumbnailIndex || 0],
                },
                pathRef,
            );
        },
        [editor, pathRef],
    );

    const visibilityMessage = (
        <Message className={margin('top')} kind={Kind.info} hasBackground>
            {translateKey(WREX_CORE.THUMBNAIL_VISIBILITY)}
        </Message>
    );

    return (
        <>
            <LinkPreview
                className={classnames(`${CLASSNAME}--editable`, {
                    [`${CLASSNAME}--selected`]: selected,
                })}
                element={element}
                onFocus={onFocus}
                tabIndex={0}
                {...forwardedProps}
            >
                <ActionButtons
                    actionRef={parentRef}
                    hasUpdateButton={isEditable}
                    onDelete={onDelete}
                    onUpdate={toggleUpdateDialog}
                    updateButtonProps={
                        element?.resource
                            ? {
                                  icon: mdiAutorenew,
                                  label: translateKey(GLOBAL.REPLACE),
                              }
                            : undefined
                    }
                />
                {children}
            </LinkPreview>

            {element?.resource ? (
                <ReplaceResourcePreviewDialog
                    isOpen={isUpdateDialogOpen}
                    onClose={closeUpdateDialog}
                    onChange={handlePreviewChange}
                    parentElement={parentRef}
                    preview={element}
                />
            ) : (
                <EditLinkPreviewDialog
                    className={className}
                    onClose={closeUpdateDialog}
                    preview={{
                        title: element?.title,
                        description: element?.description,
                        images: element?.thumbnailUrl ? [element.thumbnailUrl] : [],
                        url: element?.url,
                        thumbnailIndex: 0,
                    }}
                    isOpen={isUpdateDialogOpen}
                    onChange={handlePreviewChange}
                    editImageOptions={{
                        className: margin('top', Size.huge),
                        thumbnailProps: EXTERNAL_RESOURCE_IMG_CONFIG,
                        after: visibilityMessage,
                    }}
                    previewImgProps={EXTERNAL_RESOURCE_IMG_CONFIG}
                    parentElement={parentRef}
                />
            )}
        </>
    );
};
EditableLinkPreview.displayName = LINK_PREVIEW;

export const editableLinkPreviewWithOptions = (options: LinkPreviewEditorOptions) => {
    const Enhanced: EditableLinkPreviewType = (props) => <EditableLinkPreview {...props} {...options} />;
    Enhanced.displayName = EditableLinkPreview.displayName;

    return Enhanced;
};
